import { ReactNode } from "react";

import { ComponentWithAs, IconProps } from "@chakra-ui/react";

import { PlanEnum } from "@/__generated__/graphql";
import { PricingIcon1, PricingIcon2 } from "@/components/shared/ReleaseIcons";

export type PricingCardItem = {
  icon?: ComponentWithAs<"svg", IconProps>;
  text?: ReactNode;
  price?: ReactNode;
};

// Add core features at the top level
export const coreFeatures = [
  "Ephemeral/preview environments",
  "Fixed staging environments",
  "Production environments",
  "Cloud development environments",
  "Custom domains",
  "Full CLI access",
  "GitOps integration",
  "Source control integration",
  "Automatic environment deployment",
  "API access",
  "Full CI/CD integration",
  "Simplified onboarding with docker-compose support",
];

type ExternalFeatures = {
  exclusive: string[];
};

type PlanItem = {
  card: PricingCardItem[];
  checkmark: string[];
  description: string;
  headerText: string;
  bottomLink?: string;
  external_features?: ExternalFeatures; // Updated to only need exclusive features
};

const sandboxItems: PlanItem = {
  card: [
    {
      icon: PricingIcon1,
      text: "Start for free and upgrade anytime",
      price: "5 free hours",
    },
    {
      icon: PricingIcon2,
      text: "Access to GPUs",
      price: "Use Release's GPUs to experiment with AI",
    },
  ],
  checkmark: [
    "Easy start with no cloud setup",
    "Pre-configured AI and Web templates",
    "Train and serve models in one place",
    "Easy upgrade to Pay as you go or Enterprise plans",
  ],
  description: "No cloud or credit card required",
  headerText: "Try Release",
  external_features: {
    exclusive: [
      "Try all features free for 5 hours",
      "No credit card required",
      "Instant access to GPUs",
      "Pre-configured AI templates",
    ],
  },
};

const freeItems: PlanItem = {
  bottomLink: "https://release.com/pricing#Pre-Production-Section",

  card: [
    {
      icon: PricingIcon1,
      text: "Run your workloads without worrying about infrastructure",
      price: "Starts at $0.13 per hour",
    },
    {
      icon: PricingIcon2,
      text: "CPU and GPU resources",
      price: "Pay for only what you need",
    },
  ],
  checkmark: [
    "Full developer workflow including ephemeral environments",
    "Integration into source control and CI/CD",
    "CLI and web interface access to Release",
    "Automatic environment creation with every code commit",
  ],
  description: "Best for hands off infrastructure management",
  headerText: "Pay for compute you use",
  external_features: {
    exclusive: [
      "Free trial - 5 free compute hours",
      "Resources run in the Release Cloud",
      "Pay as you go pricing",
      "No long term commitments",
      "24/5 support",
      "Great for startups, side projects, and small teams",
    ],
  },
};

const teamsItems: PlanItem = {
  bottomLink: "https://release.com/pricing#Pre-Production-Section",

  card: [
    {
      icon: PricingIcon1,
      text: "Pre-Production Environments",
      price: "$0.69 per hour",
    },
    {
      icon: PricingIcon2,
      text: "Production Environments",
      price: "$1.04 per hour",
    },
  ],
  checkmark: [
    "Runs in your cloud",
    "Custom domains",
    "Service level agreement for support",
    "Hourly billing: only pay for what you use",
  ],
  description: "Best for growing teams and workgroups",
  headerText: "Pay as you go",
  external_features: {
    exclusive: [
      "Team collaboration features",
      "Advanced security controls",
      "Priority support",
      "Custom domains",
    ],
  },
};

const enterpriseItems: PlanItem = {
  bottomLink: "https://release.com/pricing#Production-Section",
  card: [
    {
      icon: PricingIcon1,
      text: "Workloads run in your cloud account",
      price: "Use the full breadth of resources from your cloud provider",
    },
    {
      icon: PricingIcon2,
      text: "Enterprise features for complex applications",
      price: "Support for enterprise SSO, RBAC, Helm, Terraform, and more",
    },
  ],
  checkmark: [
    "Runs in your cloud or VPC",
    "Enterprise SSO and customizable RBAC",
    "Dedicated 24/7 support",
    "Instant Datasets, environment pausing, expanded integration support including Terraform and Helm",
  ],
  description: "For teams that need to run applications in their own cloud infrastructure",
  headerText: "Pricing varies",
  external_features: {
    exclusive: [
      "Environments run in your own cloud",
      "Enterprise SSO",
      "24/7 dedicated support",
      "Dedicated Slack channel",
      "Custom SLAs",
      "Terraform and Helm support",
      "Unlimited clusters",
    ],
  },
};

export const planItems: Record<PlanEnum, PlanItem | Record<string, never>> = {
  [PlanEnum.Sandbox]: sandboxItems,
  [PlanEnum.Free]: freeItems,
  [PlanEnum.Teams]: teamsItems,
  [PlanEnum.Enterprise]: enterpriseItems,
  /* Not Used */
  [PlanEnum.Trial]: {},
  [PlanEnum.Custom]: {},
  [PlanEnum.Starter]: {},
  [PlanEnum.Professional]: {},
};
